import { MouseEvent, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  CheckMarkIcon,
  ExportIcon,
  FavoriteStarFillIcon,
  FilterIcon,
  MergeDuplicateIcon
} from "@fluentui/react-icons-mdl2";
import {
  IDetailsColumnProps,
  IDetailsColumnRenderTooltipProps,
  IDetailsHeaderProps
} from "@fluentui/react/lib/DetailsList";
import { Sticky, StickyPositionType } from "@fluentui/react/lib/Sticky";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import { IRenderFunction, memoizeFunction } from "@fluentui/react/lib/Utilities";

import { InternalFieldNames } from "Components/Overview/Grid/InternalFieldNames";
import { EntityType } from "Model/EntityType";
import { projectsFilterExpressionAdd } from "Store/Actions";
import { getTranslatedFieldLabel } from "Utils/Field";
import { Translator } from "Utils/Translator";
import { useHeaderStyles } from "./UseHeaderStyles";

const getDetailsHeaderStyles = memoizeFunction(() => mergeStyleSets({ root: { display: `inline-flex` } }));

export function onRenderDetailsHeader(
  props?: IDetailsHeaderProps,
  defaultRender?: IRenderFunction<IDetailsHeaderProps>
): JSX.Element {
  const classNames = getDetailsHeaderStyles();

  return (
    <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
      {defaultRender(
        {
          ...props,
          className: classNames.root,
          onRenderColumnHeaderTooltip
        },
        defaultRender
      )}
    </Sticky>
  );
}

export function onRenderHeader(props: IDetailsColumnProps): JSX.Element {
  return <Header {...props} />;
}

function onRenderColumnHeaderTooltip(props?: IDetailsColumnRenderTooltipProps): JSX.Element {
  const { column = { fieldName: null } } = props;
  const { fieldName } = column;
  const translator = Translator.getInstance();

  const title = getTranslatedFieldLabel(fieldName, EntityType.Project, translator);
  return <TooltipHost {...props} content={title} />;
}

const excludeFilter: string[] = [
  InternalFieldNames.TargetUrl,
  InternalFieldNames.RelevantCount,
  InternalFieldNames.AddressValidation,
  InternalFieldNames.DuplicateCheck,
  InternalFieldNames.Exported,
  InternalFieldNames.OriginId
];

const excludeContent: string[] = [InternalFieldNames.OriginId, InternalFieldNames.TargetUrl];

function Header(props: IDetailsColumnProps) {
  const {
    column: { key, name, fieldName },
    column
  } = props;
  const dispatch = useDispatch();
  const styles = useHeaderStyles();

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      dispatch(projectsFilterExpressionAdd(`{{${key}}}`));
    },
    [dispatch, key]
  );

  const renderFilter = !excludeFilter.includes(column.fieldName);
  const renderContent = !excludeContent.includes(column.fieldName);

  return (
    <>
      {renderContent && getContent(fieldName, name, styles)}
      {renderFilter && <FilterIcon className={styles.filterAddIcon} onClick={onClick} />}
    </>
  );
}

// TODO: Replace any with proper type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getContent(fieldName: string, name: string, styles: any): JSX.Element {
  switch (fieldName) {
    case InternalFieldNames.RelevantCount:
      return <FavoriteStarFillIcon className={styles.icons} />;
    case InternalFieldNames.AddressValidation:
      return <CheckMarkIcon className={styles.icons} />;
    case InternalFieldNames.DuplicateCheck:
      return <MergeDuplicateIcon className={styles.icons} />;
    case InternalFieldNames.Exported:
      return <ExportIcon className={styles.icons} />;
    default:
      return (
        <Text className={styles.text} variant="medium">
          {name}
        </Text>
      );
  }
}
